import { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { TextWrap } from "./ui/TextWrap";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const CustomerProfiles = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: "customer_id",
    direction: "asc",
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomerProfiles();
  }, []);

  const fetchCustomerProfiles = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = "customer_id",
    sortOrder = "asc"
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      console.log("Fetching customer profiles with params:", {
        page,
        perPage,
        search,
        sortBy,
        sortOrder,
      });

      const response = await apiCall(
        "get",
        `/get-all-customer-profiles?search=${encodeURIComponent(
          search
        )}&page=${page}&per_page=${perPage}&sort_by=${encodeURIComponent(
          sortBy
        )}&sort_order=${sortOrder}`,
        null,
        navigate
      );

      console.log("API Response:", response?.data);

      if (response?.data?.success) {
        let customerList = [];
        let total = 0;
        let currentPageFromResponse = page;
        let lastPage = 1;
        let perPageFromResponse = perPage;

        if (Array.isArray(response.data.data)) {
          customerList = response.data.data;
          total = response.data.pagination?.total || customerList.length;
          currentPageFromResponse =
            response.data.pagination?.current_page || page;
          lastPage = response.data.pagination?.last_page || 1;
          perPageFromResponse = response.data.pagination?.per_page || perPage;
        }

        setCustomers(customerList);
        setFilteredCustomers(customerList);
        setTotalEntries(total);
        setCurrentPage(parseInt(currentPageFromResponse, 10));
        setTotalPages(parseInt(lastPage, 10));
        setEntriesPerPage(parseInt(perPageFromResponse, 10));
      } else {
        setErrorMessage("Failed to load customer profiles.");
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching customer profiles:", error);
      setErrorMessage(error.message || "Error fetching customer profiles.");
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchCustomerProfiles = useCallback(
    debounce((page, perPage, search, sortBy, sortOrder) => {
      fetchCustomerProfiles(page, perPage, search, sortBy, sortOrder);
    }, 500),
    []
  );

  const handleSort = (field) => {
    setSortConfig((prev) => {
      const newDirection =
        prev.field === field && prev.direction === "asc" ? "desc" : "asc";
      return { field, direction: newDirection };
    });
    setCurrentPage(1); // Reset to page 1 when sorting
  };

  // Server-side sorting hai, so client-side sorting ki zarurat nahi
  const sortedCustomers = filteredCustomers;

  useEffect(() => {
    debouncedFetchCustomerProfiles(
      currentPage,
      entriesPerPage,
      searchTerm,
      sortConfig.field,
      sortConfig.direction
    );
  }, [
    currentPage,
    entriesPerPage,
    searchTerm,
    sortConfig,
    debouncedFetchCustomerProfiles,
  ]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    debouncedFetchCustomerProfiles(
      1,
      entriesPerPage,
      value,
      sortConfig.field,
      sortConfig.direction
    );
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePerPageChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const startEntry =
    totalEntries > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0;
  const endEntry = Math.min(currentPage * entriesPerPage, totalEntries);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  if (errorMessage) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by customer name..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={entriesPerPage}
              handlePerPageChange={handlePerPageChange}
            />
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {sortedCustomers.length === 0 && !loading ? (
            <TableDataNoAvailable message="No Customer Profile found." />
          ) : (
            <div className="position-relative">
              <div
                                className="table-responsive scroll-sm"

                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        <span>S.No</span>
                      </th>

                      <th
                        scope="col"
                        onClick={() => handleSort("company_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Company Name</span>
                          <SortIcons
                            field="company_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("client_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Client Name</span>
                          <SortIcons
                            field="client_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("customer_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Customer Name</span>
                          <SortIcons
                            field="customer_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("mobile_number")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Mobile Number</span>
                          <SortIcons
                            field="mobile_number"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("email")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Email</span>
                          <SortIcons field="email" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("national_id")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>National ID</span>
                          <SortIcons
                            field="national_id"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("loyalty_code")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Loyalty Code</span>
                          <SortIcons
                            field="loyalty_code"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("plate_number")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Plate Number</span>
                          <SortIcons
                            field="plate_number"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("vehicle_color")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Vehicle Color</span>
                          <SortIcons
                            field="vehicle_color"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("country_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Registered Country</span>
                          <SortIcons
                            field="country_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("authority_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Authority Name</span>
                          <SortIcons
                            field="authority_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("type_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Type Name</span>
                          <SortIcons
                            field="type_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("make_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Make Name</span>
                          <SortIcons
                            field="make_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("model_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Model Name</span>
                          <SortIcons
                            field="model_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("is_blacklisted")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex ">
                          <span>Blacklisted</span>
                          <SortIcons
                            field="is_blacklisted"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("notes")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Notes</span>
                          <SortIcons field="notes" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("created_by")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created By</span>
                          <SortIcons
                            field="created_by"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => handleSort("created_datetime")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCustomers.map((customer, index) => (
                      <tr key={customer.customer_id || index}>
                        <td className="p-2">
                          {(currentPage - 1) * entriesPerPage + index + 1}
                        </td>
                        <td className="p-2">{customer.company_name || "-"}</td>

                        <td className="p-2">{customer.client_name || "-"}</td>
                        <td className="p-2">
                          {customer.customer_name || "-"}
                        </td>
                        <td className="p-2">
                          {customer.mobile_number || "-"}
                        </td>
                        <td className="p-2">{customer.email || "-"}</td>
                        <td className="p-2">{customer.national_id || "-"}</td>
                        <td className="p-2">
                          {customer.loyalty_code || "-"}
                        </td>
                        <td className="p-2">
                          {customer.plate_number || "-"}
                        </td>
                        <td className="p-2">
                          {customer.vehicle_color || "-"}
                        </td>
                        <td className="p-2">
                          {customer.country_name || "-"}
                        </td>
                        <td className="p-2">
                          {customer.authority_name || "-"}
                        </td>
                        <td className="p-2">{customer.type_name || "-"}</td>
                        <td className="p-2">{customer.make_name || "-"}</td>
                        <td className="p-2">{customer.model_name || "-"}</td>
                        <td className="p-2">
                          <span
                              className={`px-3 py-1 radius-4 fw-medium text-sm ${
                                customer.is_blacklisted
                                  ? "bg-info-focus text-info-600 border border-info-main"
                                  : "bg-danger-focus text-danger-600 border border-danger-main"

                              }`}
                            >
                              {customer.is_blacklisted ? "Yes" : "No"}
                            </span>
                        </td>
                        <td
                          className="p-2"
                          style={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                          }}
                        >
                          <TextWrap location={customer.notes || "-"} />
                        </td>
                        <td className="p-2">{customer.created_by || "-"}</td>
                        <td className="p-2">
                          {customer.created_datetime
                            ? new Date(
                                customer.created_datetime
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}
              <PaginationControls
                pagination={{
                  currentPage,
                  perPage: entriesPerPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerProfiles;